/**
 *  @class
 *  @function MegaMenuSidebar
 *  
 */
(() => {
  const INIT_FLAG = '__wunder_init';
  const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
  const r = (n) => Math.max(0, Math.round(n)); // tame sub-pixel drift

  // map scroll<->progress using the RIGHT EDGE of the viewport:
  // pct = (scrollLeft + clientWidth) / scrollWidth * 100
  // so it starts filled to the visible portion.
  function initCarouselForWrap(wrap) {
    if (!wrap || wrap[INIT_FLAG]) return;
    wrap[INIT_FLAG] = true;

    const list = wrap.querySelector('.mega-menu-sidebar--content-inner');
    const progress = wrap.querySelector('.carousel-progress');
    const range = wrap.querySelector('.carousel-range');
    const fill = wrap.querySelector('.carousel-progress-fill');
    if (!list || !progress || !range || !fill) return;

    const items = Array.from(list.children).filter((el) => el.tagName === 'LI');
    const hasCarousel = items.length > 3;

    // Not enough items: keep slider hidden
    if (!hasCarousel) {
      progress.setAttribute('aria-hidden', 'true');
      return;
    }

    // Activate carousel UI
    list.classList.add('is-carousel');
    wrap.classList.add('is-carousel');
    progress.setAttribute('aria-hidden', 'false');

    // Keyboard focus
    if (!list.hasAttribute('tabindex')) list.setAttribute('tabindex', '0');

    // Reset start state
    list.scrollLeft = 0;

    const maxScroll = () => Math.max(0, r(list.scrollWidth - list.clientWidth));
    const pctRight = () => {
      const sw = list.scrollWidth || 1;
      return clamp(((list.scrollLeft + list.clientWidth) / sw) * 100, 0, 100);
    };

    // Reflect scroll -> progress (fill + hidden range value)
    const updateFromScroll = () => {
      const pct = pctRight();
      // keep the hidden range value in sync for keyboard & drag
      range.value = String(pct);
      fill.style.width = pct + '%';
    };

    // When dragging/clicking the (invisible) range, scroll so that
    // the RIGHT EDGE maps to the slider value.
    const scrollFromRange = (immediate = false) => {
      const pct = clamp(parseFloat(range.value || '0'), 0, 100);
      const sw = list.scrollWidth || 1;
      const desiredRight = (pct / 100) * sw;                // pixels
      const desiredLeft = desiredRight - list.clientWidth;  // align right edge
      const left = clamp(desiredLeft, 0, maxScroll());
      list.scrollTo({ left, behavior: immediate ? 'auto' : 'smooth' });
      fill.style.width = pct + '%';
    };

    // Start partially filled to the visible portion
    updateFromScroll();

    // Input handlers: keep it smooth while moving, snap on commit
    range.addEventListener('input', () => scrollFromRange(false));
    range.addEventListener('change', () => {
      // optional: snap to nearest card width on commit
      const first = items[0];
      const step = (() => {
        if (!first) return list.clientWidth;
        const rect = first.getBoundingClientRect();
        const style = window.getComputedStyle(list);
        const gap = parseFloat(style.columnGap || style.gap || 0);
        return rect.width + gap;
      })();
      const target = Math.round(list.scrollLeft / step) * step;
      list.scrollTo({ left: target, behavior: 'smooth' });
      requestAnimationFrame(() => setTimeout(updateFromScroll, 150));
    });

    // Keep in sync while user scrolls
    list.addEventListener('scroll', updateFromScroll, { passive: true });

    // Keep in sync on resize/content change
    const refresh = () => {
      list.scrollLeft = r(list.scrollLeft);
      updateFromScroll();
    };
    window.addEventListener('resize', refresh);
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(refresh);
      ro.observe(list);
    }
    document.addEventListener('lazyloaded', refresh, { passive: true });

    // First paint sync (after layout settles)
    requestAnimationFrame(refresh);
  }

  function initAllWraps(root) {
    root
      .querySelectorAll('.mega-menu-sidebar--content .carousel-wrap')
      .forEach(initCarouselForWrap);
  }

  function resetAndInitForList(listEl) {
    if (!listEl) return;
    const wrap = listEl.closest('.carousel-wrap');
    if (!wrap) return;
    listEl.scrollLeft = 0;
    initCarouselForWrap(wrap);
    requestAnimationFrame(() => listEl.dispatchEvent(new Event('scroll')));
  }

  if (!customElements.get('mega-menu-sidebar')) {
    class MegaMenuSidebar extends HTMLElement {
      constructor() {
        super();
        this._onHover = this._onHover.bind(this);
      }

      connectedCallback() {
        this.buttons = this.querySelectorAll('.mega-menu-sidebar--button');
        this.contentLists = this.querySelectorAll('.mega-menu-sidebar--content-inner');

        // Hover switches tabs
        this.buttons.forEach((button, i) => {
          button.addEventListener('mouseover', (event) => this._onHover(event, button, i));
        });

        // Init active list
        const activeList =
          this.querySelector('.mega-menu-sidebar--content-inner.active') ||
          this.contentLists[0];
        resetAndInitForList(activeList);
        requestAnimationFrame(() => initAllWraps(this));

        // Observe DOM changes (drawer open/close, active flips)
        if (typeof MutationObserver !== 'undefined') {
          const mo = new MutationObserver(() => {
            this.querySelectorAll('.mega-menu-sidebar--content-inner.active')
              .forEach(resetAndInitForList);
          });
          mo.observe(this, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class'],
          });
        }

        // Click-based tab switching too
        this.addEventListener('click', (e) => {
          const btn = e.target.closest('.mega-menu-sidebar--button[data-target]');
          if (!btn) return;
          const sel = btn.getAttribute('data-target');
          const list = this.querySelector(sel);
          if (!list) return;

          this.contentLists.forEach((el) => el.classList.toggle('active', el === list));
          this.buttons.forEach((b) => b.classList.toggle('active', b === btn));

          resetAndInitForList(list);
        });
      }

      _onHover(_event, button, index) {
        this.contentLists.forEach((el, i) => {
          el.classList.toggle('active', i === index);
        });
        this.buttons.forEach((b) => b.classList.remove('active'));
        button.classList.add('active');

        const list = this.contentLists[index];
        resetAndInitForList(list);
      }
    }

    customElements.define('mega-menu-sidebar', MegaMenuSidebar);
  }

  const boot = () =>
    document.querySelectorAll('mega-menu-sidebar').forEach((el) => initAllWraps(el));

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

    /* -------------------------------------------------------------
     Hover opacity logic (dim others)
  ------------------------------------------------------------- */
  function initHoverDim(root) {
    root.querySelectorAll('.mega-menu-sidebar--content-inner').forEach((list) => {
      const items = list.querySelectorAll('li');
      items.forEach((item) => {
        item.addEventListener('mouseenter', () => {
          list.classList.add('hover-active');
          items.forEach((li) => li.classList.remove('is-hovered'));
          item.classList.add('is-hovered');
        });
        item.addEventListener('mouseleave', () => {
          item.classList.remove('is-hovered');
          list.classList.remove('hover-active');
        });
      });
    });
  }

  // run hover effect setup after everything initializes
  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('mega-menu-sidebar').forEach(initHoverDim);
  });

})();
